// @flow
import { createRef, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  AddressPicker,
  ADDRESS_NOT_FOUND_ID,
  type Address as AddressType,
  type AddressNotFound,
} from "@nested/brand";
import { sendAnalyticsEvent } from "@nested/analytics";
import { SELECT_PROPERTY } from "@nested/analytics/events";
import { Heading, QuestionDescription, QuestionWrapper } from "../components";
import { useDesktopFocus } from "../hooks/useDesktopFocus";
import { usePrefilledAddress } from "../hooks/usePrefilledAddress";
import { type QuestionProps } from "../components/MultiStageForm";
import { validatePresence } from "./validators";

const validate = validatePresence(
  "address",
  "Please enter your postcode then select your address from the list",
);

type AddressProps = {
  allowAddressNotFound?: boolean,
  ...QuestionProps<"address">,
};

export const AddressAllowNotFound = (props: AddressProps) => (
  <Address {...props} allowAddressNotFound />
);

export const Address = ({
  ActionButtons,
  BackButton,
  initialValue,
  allowAddressNotFound = false,
  next,
  valid,
  validationErrors,
}: AddressProps) => {
  const history = useHistory();
  const [address, setAddress] = useState<AddressType | AddressNotFound | null>(
    initialValue || null,
  ); // eslint-disable-line camelcase, max-len

  const { address: prefilledAddress, loading } =
    usePrefilledAddress(allowAddressNotFound);
  const textInputRef = createRef<HTMLInputElement>();

  /**
   * Auto focusing on a prefilled address is a bad experience, because you can't
   * see the address you've selected previously. Hence the checks.
   */
  useDesktopFocus(textInputRef, Boolean(initialValue || prefilledAddress));

  useEffect(() => {
    if (prefilledAddress) {
      next(prefilledAddress, validate, { prefilled: true });
    }
  }, [prefilledAddress]);

  // Prevents the question flashing up before redirecting on prefill
  if (loading || prefilledAddress) return null;

  return (
    <QuestionWrapper
      next={() => {
        // Validate the address isn't empty before firing, like on page load.
        if (address) {
          const {
            id,
            blacklisted,

            postcode,
            postcodeInServicedArea,
          } = address;
          // Its possible to get to this page with an unserviced postcode in the address picker
          // if the address is in redux state and the user returns or gets directed to /get-started.
          if (blacklisted) {
            history.push(
              `/get-started/postcode-not-in-serviced-area?postcode=${postcode}&addressId=${id}`,
            );
          } else if (!postcodeInServicedArea) {
            history.push(
              `/get-started/coming-soon?postcode=${postcode}&addressId=${id}`,
            );
          } else {
            if (address.id === ADDRESS_NOT_FOUND_ID) {
              sendAnalyticsEvent({
                event: SELECT_PROPERTY,
                id,
                postcode,
              });
            } else {
              const {
                currentAgent,
                currentPrice,
                currentStatus,
                isListed,
                label,
                weeksOnMarket,
              } = address;

              sendAnalyticsEvent({
                event: SELECT_PROPERTY,
                id,
                isListed,
                label,
                postcode,
                currentAgent,
                currentPrice,
                currentStatus,
                weeksOnMarket,
              });
            }
            next(address, validate);
          }
        }
      }}
      dataTest="what-is-your-address-question"
    >
      <BackButton />
      <Heading>What is your full address?</Heading>
      <QuestionDescription>
        We don’t share your personal details with anyone.
      </QuestionDescription>
      <AddressPicker
        errorMessage={validationErrors.address}
        hasErrors={!valid}
        name="address"
        onChange={(a) => {
          setAddress(a);
          if (a.id === ADDRESS_NOT_FOUND_ID) {
            next(a, validate);
          }
        }}
        placeholder="Enter your postcode"
        ref={textInputRef}
        allowAddressNotFound={allowAddressNotFound}
        value={address}
        prefill={
          initialValue
            ? {
                addressId: initialValue.id,
                postcode: initialValue.postcode,
              }
            : undefined
        }
      />
      <ActionButtons />
    </QuestionWrapper>
  );
};
